import { splitHighlighted, type CheckCard as CheckCardData } from '@/lib/check/quizzes';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function Highlighted({
  text,
  highlights,
}: {
  text: string;
  highlights: string[];
}) {
  return (
    <>
      {splitHighlighted(text, highlights).map((part, i) =>
        part.hi ? (
          <strong key={i} className='font-bold text-primary'>
            {part.text}
          </strong>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}

export function CheckCard({
  card,
  kicker,
}: {
  card: CheckCardData;
  kicker?: string;
}) {
  const pos = Math.min(100, Math.max(0, card.scale_position * 100));
  return (
    <article className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8'>
      <p
        className='text-xs font-semibold uppercase tracking-wide text-gray-500'
        style={nunito}
      >
        {kicker || card.kicker || 'Harbor check'}
      </p>
      <h2
        className='mt-2 text-2xl font-bold capitalize text-gray-900 md:text-3xl'
        style={{ ...nunito, fontWeight: 700 }}
      >
        {card.type_title}
      </h2>
      <p className='mt-4 text-lg leading-relaxed text-gray-800' style={nunito}>
        <Highlighted text={card.identity_line} highlights={card.identity_highlights} />
      </p>
      <p className='mt-2 text-base leading-relaxed text-gray-600' style={nunito}>
        <Highlighted text={card.trait_line} highlights={card.trait_highlights} />
      </p>
      <div className='mt-8'>
        <div className='relative h-2 rounded-full bg-gray-200'>
          <div
            className='absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary'
            style={{ left: `${pos}%` }}
            aria-hidden
          />
        </div>
        <div
          className='mt-3 flex justify-between text-xs text-gray-500'
          style={nunito}
        >
          <span>{card.scale_left}</span>
          <span>{card.scale_mid}</span>
          <span>{card.scale_right}</span>
        </div>
      </div>
    </article>
  );
}

export function CheckHistogram({
  card,
}: {
  card: CheckCardData;
}) {
  const max = Math.max(1, ...card.histogram.map((b) => b.count));
  const userBucket = Math.min(9, Math.max(0, Math.floor(card.scale_position * 10)));
  return (
    <div className='mt-8'>
      <p className='mb-3 text-sm font-semibold text-gray-700' style={nunito}>
        You vs {card.pool_label}
        {card.early_look ? ' · early look' : ''}
      </p>
      <div className='flex h-24 items-end gap-1'>
        {card.histogram.map((bucket, i) => (
          <div
            key={bucket.bucket}
            className={`flex-1 rounded-t ${i === userBucket ? 'bg-primary' : 'bg-primary/35'}`}
            style={{ height: `${Math.max(16, (bucket.count / max) * 100)}%` }}
            title={`${bucket.bucket}: ${bucket.count}`}
          />
        ))}
      </div>
    </div>
  );
}
